import React from 'react';
import PropTypes from 'prop-types';
import { Icon, Tooltip } from '@syneto/compass-react';
import { VALIDITY_STATUS } from '../../../helpers/ExpiryRule';

// Expiring keeps the Dashboard's warning triangle (MaintenanceStatus.js). Expired is a red info circle on purpose:
// a lapsed add-on is informational, so it gets a calmer shape than the warning, and it stays distinct from the
// OS update icon (a yellow `far fa-exclamation-circle` outline circle).
const ICON_BY_STATUS = Object.freeze({
	[VALIDITY_STATUS.EXPIRING]: { name: 'fas fa-exclamation-triangle', color: '#F4BE00' },
	[VALIDITY_STATUS.EXPIRED]: { name: 'fas fa-info-circle', color: '#E45664' },
});

// The tooltip text alone does not say that a click does something, so the clickable variant adds it to the
// accessible name. No `trigger` is passed to the compass Tooltip on purpose: it then uses react-bootstrap's default
// (hover and focus), so a keyboard user sees the text too (InfoIcon passes 'hover' and is hover-only).
const CLICK_HINT = 'Show machine details.';

export const ExpiryAlertIcon = (props) => {
	const { status, text, placement, onClick } = props;
	const { name, color } = ICON_BY_STATUS[status];
	const accessibleName = onClick ? `${text}. ${CLICK_HINT}` : text;

	const handleKeyDown = (event) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			onClick(event);
		}
	};

	const interactionProps = onClick
		? { role: 'button', tabIndex: 0, onClick, onKeyDown: handleKeyDown, style: { cursor: 'pointer' } }
		: { role: 'img' };

	return (
		<Tooltip placement={placement} text={text} theme="dark">
			<span aria-label={accessibleName} {...interactionProps}>
				<Icon name={name} size="lg" color={color} />
			</span>
		</Tooltip>
	);
};

ExpiryAlertIcon.propTypes = {
	status: PropTypes.oneOf([VALIDITY_STATUS.EXPIRING, VALIDITY_STATUS.EXPIRED]).isRequired,
	text: PropTypes.string.isRequired,
	placement: PropTypes.string,
	onClick: PropTypes.func,
};

ExpiryAlertIcon.defaultProps = {
	placement: 'right',
};
